import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Mail, Lock, User, GraduationCap, BookOpen, ArrowRight } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../store/auth';
import { InlineLoader } from '../../components/ui/PremiumLoader';
import { cn } from '../../lib/utils';
import { useAnalytics, AnalyticsEvents } from '../../hooks/useAnalytics';

const registerSchema = z.object({
  firstName: z.string().min(1, 'First name is required').max(100),
  lastName: z.string().min(1, 'Last name is required').max(100),
  email: z.string().email('Please enter a valid email'),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number')
    .regex(/[^A-Za-z0-9]/, 'Password must contain at least one special character'),
  confirmPassword: z.string(),
  role: z.enum(['STUDENT', 'PROFESSOR']),
}).refine((data) => data.password === data.confirmPassword, {
  message: 'Passwords do not match',
  path: ['confirmPassword'],
});

type RegisterForm = z.infer<typeof registerSchema>;

const fadeUp = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
};

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register: registerUser, isLoading } = useAuthStore();
  const { trackEvent } = useAnalytics();
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<RegisterForm>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      role: 'STUDENT',
    },
  });

  const selectedRole = watch('role');

  const onSubmit = async (data: RegisterForm) => {
    try {
      await registerUser({
        email: data.email,
        password: data.password,
        firstName: data.firstName,
        lastName: data.lastName,
        role: data.role,
      });
      // Track successful registration (no PII)
      trackEvent(AnalyticsEvents.USER_SIGNED_UP, {
        role: data.role,
        method: 'email',
      });
      navigate('/verify-email');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Registration failed';
      toast.error(message);
    }
  };

  return (
    <motion.div
      initial="initial"
      animate="animate"
      className="space-y-6"
    >
      {/* Header */}
      <motion.div variants={fadeUp} transition={{ duration: 0.4 }}>
        <h1 className="text-3xl font-bold text-white">Create Account</h1>
        <p className="mt-2 text-white/50">Start your learning journey today</p>
      </motion.div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        {/* Role Selection */}
        <motion.div variants={fadeUp} transition={{ duration: 0.4, delay: 0.05 }}>
          <label className="label">I want to</label>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setValue('role', 'STUDENT')}
              className={cn(
                'relative p-4 rounded-xl border transition-all duration-200 text-left group',
                selectedRole === 'STUDENT'
                  ? 'border-aurora/50 bg-aurora/10'
                  : 'border-white/10 hover:border-white/20 bg-white/[0.02]'
              )}
            >
              <div className={cn(
                'w-10 h-10 rounded-lg flex items-center justify-center mb-2.5 transition-colors',
                selectedRole === 'STUDENT' ? 'bg-aurora/20' : 'bg-white/5'
              )}>
                <GraduationCap className={cn(
                  'w-5 h-5',
                  selectedRole === 'STUDENT' ? 'text-aurora-light' : 'text-white/40'
                )} />
              </div>
              <div className={cn(
                'font-semibold text-sm',
                selectedRole === 'STUDENT' ? 'text-white' : 'text-white/70'
              )}>
                Learn
              </div>
              <div className="text-xs text-white/40 mt-0.5">Access courses and grow</div>
            </button>

            <button
              type="button"
              onClick={() => setValue('role', 'PROFESSOR')}
              className={cn(
                'relative p-4 rounded-xl border transition-all duration-200 text-left group',
                selectedRole === 'PROFESSOR'
                  ? 'border-aurora/50 bg-aurora/10'
                  : 'border-white/10 hover:border-white/20 bg-white/[0.02]'
              )}
            >
              <div className={cn(
                'w-10 h-10 rounded-lg flex items-center justify-center mb-2.5 transition-colors',
                selectedRole === 'PROFESSOR' ? 'bg-aurora/20' : 'bg-white/5'
              )}>
                <BookOpen className={cn(
                  'w-5 h-5',
                  selectedRole === 'PROFESSOR' ? 'text-aurora-light' : 'text-white/40'
                )} />
              </div>
              <div className={cn(
                'font-semibold text-sm',
                selectedRole === 'PROFESSOR' ? 'text-white' : 'text-white/70'
              )}>
                Teach
              </div>
              <div className="text-xs text-white/40 mt-0.5">Create and sell courses</div>
            </button>
          </div>
        </motion.div>

        {/* Name fields - masked from analytics */}
        <motion.div variants={fadeUp} transition={{ duration: 0.4, delay: 0.1 }} className="grid grid-cols-2 gap-3 ph-no-capture">
          <div>
            <label htmlFor="firstName" className="label">First Name</label>
            <div className="relative group">
              <input
                id="firstName"
                type="text"
                {...register('firstName')}
                className={`input pl-10 ${errors.firstName ? 'input-error' : ''}`}
                placeholder="John"
                data-ph-no-capture
              />
              <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30
                             group-focus-within:text-aurora transition-colors" />
            </div>
            {errors.firstName && (
              <p className="mt-1.5 text-xs text-error">{errors.firstName.message}</p>
            )}
          </div>
          <div>
            <label htmlFor="lastName" className="label">Last Name</label>
            <input
              id="lastName"
              type="text"
              {...register('lastName')}
              className={`input ${errors.lastName ? 'input-error' : ''}`}
              placeholder="Doe"
              data-ph-no-capture
            />
            {errors.lastName && (
              <p className="mt-1.5 text-xs text-error">{errors.lastName.message}</p>
            )}
          </div>
        </motion.div>

        {/* Email - masked from analytics */}
        <motion.div variants={fadeUp} transition={{ duration: 0.4, delay: 0.15 }} className="ph-no-capture">
          <label htmlFor="email" className="label">Email</label>
          <div className="relative group">
            <input
              id="email"
              type="email"
              {...register('email')}
              className={`input pl-10 ${errors.email ? 'input-error' : ''}`}
              placeholder="you@example.com"
              data-ph-no-capture
            />
            <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30
                           group-focus-within:text-aurora transition-colors" />
          </div>
          {errors.email && (
            <p className="mt-1.5 text-xs text-error">{errors.email.message}</p>
          )}
        </motion.div>

        {/* Password - masked from analytics */}
        <motion.div variants={fadeUp} transition={{ duration: 0.4, delay: 0.2 }} className="ph-no-capture">
          <label htmlFor="password" className="label">Password</label>
          <div className="relative group">
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              {...register('password')}
              className={`input pl-10 pr-10 ${errors.password ? 'input-error' : ''}`}
              placeholder="••••••••"
              data-ph-no-capture
            />
            <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30
                           group-focus-within:text-aurora transition-colors" />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-white/30 
                       hover:text-white/60 transition-colors"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {errors.password && (
            <p className="mt-1.5 text-xs text-error">{errors.password.message}</p>
          )}
        </motion.div>

        {/* Confirm Password - masked from analytics */}
        <motion.div variants={fadeUp} transition={{ duration: 0.4, delay: 0.25 }} className="ph-no-capture">
          <label htmlFor="confirmPassword" className="label">Confirm Password</label>
          <input
            id="confirmPassword"
            type="password"
            {...register('confirmPassword')}
            className={`input ${errors.confirmPassword ? 'input-error' : ''}`}
            placeholder="••••••••"
            data-ph-no-capture
          />
          {errors.confirmPassword && (
            <p className="mt-1.5 text-xs text-error">{errors.confirmPassword.message}</p>
          )}
        </motion.div>

        {/* Submit */}
        <motion.div variants={fadeUp} transition={{ duration: 0.4, delay: 0.3 }}>
          <button type="submit" disabled={isLoading} className="btn-primary w-full group">
            {isLoading ? (
              <>
                <InlineLoader className="mr-2" />
                <span>Creating account...</span>
              </>
            ) : (
              <>
                <span>Create Account</span>
                <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
              </>
            )}
          </button>
        </motion.div>

        {/* Terms */}
        <motion.p variants={fadeUp} transition={{ duration: 0.4, delay: 0.35 }} className="text-xs text-white/30 text-center">
          By creating an account, you agree to our{' '}
          <a href="#" className="text-aurora-light hover:text-aurora">Terms of Service</a>
          {' '}and{' '}
          <a href="#" className="text-aurora-light hover:text-aurora">Privacy Policy</a>
        </motion.p>
      </form>

      {/* Login link */}
      <motion.p variants={fadeUp} transition={{ duration: 0.4, delay: 0.4 }} className="text-center text-white/50">
        Already have an account?{' '}
        <Link to="/login" className="text-aurora-light hover:text-aurora font-medium transition-colors">
          Sign in
        </Link>
      </motion.p>
    </motion.div>
  );
}
